import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Heart, User, ShoppingBag, MapPin, Clock, CheckCircle, Truck, CreditCard, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

const statusConfig = {
  pending: { label: 'Pendente', color: 'bg-yellow-500', icon: Clock },
  confirmed: { label: 'Confirmado', color: 'bg-blue-500', icon: CheckCircle },
  shipped: { label: 'Enviado', color: 'bg-purple-500', icon: Truck },
  delivered: { label: 'Entregue', color: 'bg-green-500', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: 'bg-red-500', icon: Clock }
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recentOrders, setRecentOrders] = useState([]);
  const [stats, setStats] = useState({ totalOrders: 0, totalSpent: 0, wishlistCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [ordersRes, wishlistRes] = await Promise.all([
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/wishlist`, { withCredentials: true })
      ]);

      const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      const visibleOrders = orders.filter((o) => o && o.status !== 'cancelled');
      let recent = visibleOrders.slice(0, 3);
      setRecentOrders(recent);

      const pending = recent.filter((o) => o && o.payment_status === 'pending' && o.status === 'pending');
      if (pending.length > 0) {
        try {
          const syncResp = await axios.post(
            `${API}/mercadopago/sync-orders`,
            { order_ids: pending.map((o) => o.id) },
            { withCredentials: true, timeout: 20000 }
          );
          const results = syncResp?.data?.results || [];
          if (Array.isArray(results) && results.length > 0) {
            recent = recent.map((o) => {
              const r = results.find((x) => x.order_id === o.id);
              if (!r || !r.synced) return o;
              if (r.payment_status && r.order_status) {
                return { ...o, payment_status: r.payment_status, status: r.order_status };
              }
              return o;
            });
            setRecentOrders(recent);
          }
        } catch (e) {
          // não bloqueia a tela se o sync falhar
        }
      }

      const totalSpent = orders
        .filter(o => o.status !== 'cancelled')
        .filter(o => o.payment_status === 'paid')
        .reduce((sum, o) => sum + o.total, 0);

      setStats({
        totalOrders: visibleOrders.length,
        totalSpent,
        wishlistCount: wishlistRes.data.product_ids?.length || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const menuItems = [
    { title: 'Meus Pedidos', icon: Package, path: '/orders', description: 'Acompanhe seus pedidos', color: 'from-blue-500 to-blue-600' },
    { title: 'Favoritos', icon: Heart, path: '/wishlist', description: 'Produtos salvos', color: 'from-pink-500 to-rose-600' },
    { title: 'Endereços', icon: MapPin, path: '/addresses', description: 'Gerenciar endereços', color: 'from-emerald-500 to-emerald-600' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-8">
      <div className="container mx-auto px-4">
        {/* Header com perfil */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <Avatar className="h-24 w-24 border-4 border-blue-500">
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-2xl">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-1" data-testid="dashboard-title">
                Olá, {user.name}! 👋
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-4">{user.email}</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/30 px-4 py-2 rounded-xl">
                  <p className="text-sm text-blue-600 dark:text-blue-400">Total de Pedidos</p>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.totalOrders}</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/30 px-4 py-2 rounded-xl">
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">Total Gasto</p>
                  <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(stats.totalSpent)}</p>
                </div>
                <div className="bg-pink-50 dark:bg-pink-900/30 px-4 py-2 rounded-xl">
                  <p className="text-sm text-pink-600 dark:text-pink-400">Favoritos</p>
                  <p className="text-2xl font-bold text-pink-700 dark:text-pink-300">{stats.wishlistCount}</p>
                </div>
              </div>
            </div>
            <Button onClick={() => navigate('/shop')} size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <ShoppingBag className="mr-2 h-5 w-5" />
              Continuar Comprando
            </Button>
          </div>
        </div>

        {/* Menu rápido */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {menuItems.map((item) => (
            <Card
              key={item.path}
              className="cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden group"
              onClick={() => navigate(item.path)}
              data-testid={`dashboard-card-${item.path.replace('/', '')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                    <item.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">{item.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{item.description}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pedidos recentes */}
        <Card className="shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-xl">Pedidos Recentes</CardTitle>
              <CardDescription>Seus últimos pedidos na loja</CardDescription>
            </div>
            <Button variant="outline" onClick={() => navigate('/orders')}>
              Ver Todos
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : recentOrders.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">Você ainda não fez nenhum pedido</p>
                <Button onClick={() => navigate('/shop')} className="mt-4">
                  Começar a Comprar
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentOrders.map((order) => {
                  const StatusIcon = statusConfig[order.status]?.icon || Clock;
                  return (
                    <div 
                      key={order.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 ${statusConfig[order.status]?.color} bg-opacity-20 rounded-xl flex items-center justify-center`}>
                          <StatusIcon className={`h-5 w-5 ${statusConfig[order.status]?.color.replace('bg-', 'text-')}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">Pedido #{order.id.slice(0, 8)}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{formatDate(order.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900 dark:text-white">{formatCurrency(order.total)}</p>
                        <Badge className={`${statusConfig[order.status]?.color} text-white`}>
                          {statusConfig[order.status]?.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};